"use client"
import { useState, useEffect } from "react"
import { Mail, Phone, Facebook, Linkedin, ChevronDown, CheckCircle } from "lucide-react"
import LandingDemoStudyBuddy from "./landing-demo-studybudy"
import LandingDemoAssignment from "./landing-demo-assignment"

export default function LandingPage() {
  const [openAccordion, setOpenAccordion] = useState(null)
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false)
  const [showAbout, setShowAbout] = useState(false)
  const [demoMode, setDemoMode] = useState("study-buddy")

  // Demo Usage Tracking
  const [studyBuddyDemoUsed, setStudyBuddyDemoUsed] = useState(false)
  const [assignmentDemoUsed, setAssignmentDemoUsed] = useState(false)

  // NEW: Track if demo was used at the time of component rendering
  const [studyBuddyDemoUsedAtRender, setStudyBuddyDemoUsedAtRender] = useState(false)
  const [assignmentDemoUsedAtRender, setAssignmentDemoUsedAtRender] = useState(false)

  useEffect(() => {
    // Initialize demo flags from localStorage on component mount
    const sbUsed = localStorage.getItem("studyBuddyDemoUsed")
    if (sbUsed === null) {
      localStorage.setItem("studyBuddyDemoUsed", "false")
      setStudyBuddyDemoUsed(false)
      setStudyBuddyDemoUsedAtRender(false) // NEW: Set render-time state
    } else {
      const isUsed = sbUsed === "true"
      setStudyBuddyDemoUsed(isUsed)
      setStudyBuddyDemoUsedAtRender(isUsed) // NEW: Set render-time state
    }

    const assignUsed = localStorage.getItem("assignmentDemoUsed")
    if (assignUsed === null) {
      localStorage.setItem("assignmentDemoUsed", "false")
      setAssignmentDemoUsed(false)
      setAssignmentDemoUsedAtRender(false) // NEW: Set render-time state
    } else {
      const isUsed = assignUsed === "true"
      setAssignmentDemoUsed(isUsed)
      setAssignmentDemoUsedAtRender(isUsed) // NEW: Set render-time state
    }
  }, [])

  const toggleAccordion = (value) => {
    setOpenAccordion(openAccordion === value ? null : value)
  }

  const showOverallDemoLimit = studyBuddyDemoUsed && assignmentDemoUsed

  const scrollToSection = (id) => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }

  // NEW: Component to show demo used message for Study Buddy
  const StudyBuddyDemoUsedMessage = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center h-[400px] sm:h-[600px] border border-gray-200 rounded-lg bg-gray-50">
      <h4 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Study Buddy Demo Used!</h4>
      <p className="text-orange-600 font-semibold mb-4">You've already used your free Study Buddy demo.</p>
      <p className="text-gray-500 text-sm mb-4">Please login/signup to continue using Study Buddy.</p>
      <a href="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors">
        Login / Sign Up
      </a>
    </div>
  )

  // NEW: Component to show demo used message for Assignment
  const AssignmentDemoUsedMessage = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center h-[400px] sm:h-[600px] border border-gray-200 rounded-lg bg-gray-50">
      <h4 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Assignment Demo Used!</h4>
      <p className="text-orange-600 font-semibold mb-4">You've already used your free Assignment demo.</p>
      <p className="text-gray-500 text-sm mb-4">Please login/signup to continue using Assignment Creator.</p>
      <a href="/login" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors">
        Login / Sign Up
      </a>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#EBF0F9]">
      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 sm:px-8 py-6 text-white">
        <div className="flex items-center space-x-4 sm:space-x-12">
          <div className="flex items-center space-x-2">
            <div className="logo-name mt-2">
              <a href="/" className="flex items-center gap-2 py-2">
                <div>
                  <h1 className="flex text-xl sm:text-2xl md:text-3xl font-semibold text-white">
                    <span>पढ़.</span>
                    <span className="gradient-text font-semibold">AI</span>
                  </h1>
                </div>
              </a>
            </div>
          </div>
          <div className="hidden md:flex space-x-8">
            <a
              href="#demo-section"
              onClick={(e) => {
                e.preventDefault()
                scrollToSection("demo-section")
              }}
              className="hover:text-gray-300 text-white text-sm lg:text-base"
            >
              Try Demo
            </a>
            <a
              href="#features-section"
              onClick={(e) => {
                e.preventDefault()
                scrollToSection("features-section")
              }}
              className="hover:text-gray-300 text-white text-sm lg:text-base"
            >
              Features
            </a>
          </div>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          <a
            href="/login"
            className="text-white hover:text-gray-300 px-2 sm:px-4 py-2 rounded-full transition-colors text-sm lg:text-base"
          >
            Sign In
          </a>
          <a
            href="/signup"
            className="bg-red-500 hover:bg-red-600 text-white px-3 sm:px-6 py-2 rounded-full transition-colors text-sm lg:text-base"
          >
            Sign up
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white min-h-screen">
        {/* Background gradient image */}
        <div className="absolute inset-0 -top-48">
          <img
            src="/images/gradient-background.png"
            alt="Gradient background"
            className="object-cover w-full h-[130vh] absolute inset-0"
            width={1920}
            height={1080}
          />
        </div>
        <div className="relative z-10">
          <div className="container mx-auto px-4 sm:px-8 py-20 pt-28">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center min-h-[600px]">
              {/* Left side - Text content */}
              <div className="space-y-6 text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                  From Chalkboards
                  <br />
                  to Chatbots
                </h1>
                <p className="text-base sm:text-lg text-white/90 max-w-lg leading-relaxed mx-auto lg:mx-0">
                  Padhai by Aura brings you a suite of AI tools created for educators. Create Assignments, Quizzes,
                  Courses or live classes all in one easy platform so you can spend more time teaching.
                </p>
                <a
                  href="/bookADemo"
                  className="bg-gray-900 hover:bg-gray-800 text-white px-6 sm:px-8 py-3 text-base sm:text-lg rounded-full transition-colors inline-flex items-center justify-center"
                >
                  Book Your Demo →
                </a>
              </div>
              {/* Right side - Empty space for absolute positioned image */}
              <div className="relative hidden lg:block"></div>
            </div>
          </div>
          {/* Hero students image - positioned absolutely to not affect text layout */}
          <div className="absolute top-0 right-0 hidden lg:block" style={{ top: "-150px", right: "-200px" }}>
            <img
              src="/images/hero-students.png"
              alt="Students reading on stacked books with educational elements"
              className="relative z-10"
              style={{
                filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.1))",
                width: "1200px",
                height: "1267px",
                objectFit: "contain",
              }}
              width={1200}
              height={1267}
            />
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-40 right-24 w-20 h-20 bg-red-400 rounded-lg rotate-45 opacity-90 z-20 hidden lg:block"></div>
      </section>

      {/* Key Features Section */}
      <section id="features-section" className="py-12 sm:py-20 bg-gray-50 relative">
        {/* Background decorative vectors */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute top-20 left-10 w-32 h-32 text-gray-200" viewBox="0 0 100 100" fill="none">
            <path d="M10 10 L90 90 M90 10 L10 90" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
          </svg>
          <svg className="absolute bottom-20 right-10 w-40 h-40 text-gray-200" viewBox="0 0 100 100" fill="none">
            <path d="M20 20 L80 50 L20 80" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
          </svg>
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          {/* Generate Question Feature - Content Left, Image Right */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-16">
            <div className="space-y-6 order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-bold text-blue-600">Generate Question</h2>
              <p
                className="text-gray-600 leading-[150%] text-base sm:text-lg lg:text-[20px]"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                Create assignments, quizzes, and essays in minutes using any text, topic, or material. No more
                late-night lesson planning or manual question setting. With AI-powered generation, get high-quality
                academic content instantly. Customize difficulty, formats, and topics as per your needs.
              </p>
            </div>
            <div className="relative order-1 lg:order-2">
              <img
                src="/images/ai-assistant.png"
                alt="AI Assistant - Woman using tablet with AI elements"
                className="w-full h-auto rounded-2xl"
                width={600}
                height={400}
              />
            </div>
          </div>

          {/* Study Buddy Chat Feature - Chat Left, Content Right */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-20 rounded-3xl p-4 sm:p-8 relative overflow-hidden">
            {/* Left side - Chat Interface */}
            <div className="relative z-10 flex flex-col items-end space-y-4 sm:space-y-6 p-4 sm:p-6 w-full min-h-[400px] justify-between order-2 lg:order-1">
              {/* Message 1: Study Buddy */}
              <div className="flex items-start space-x-4 w-full">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/study-buddy-avatar.png"
                    alt="Study Buddy Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
                <div className="bg-gray-100 p-3 rounded-xl rounded-tl-none max-w-[75%]">
                  <p className="text-sm sm:text-lg text-gray-800">Hi Vipul. How can I help you?</p>
                  <p className="text-xs sm:text-base text-gray-500 mt-1">Study Buddy, 12:35</p>
                </div>
              </div>
              {/* Message 2: Vipul */}
              <div className="flex items-end justify-end space-x-4 w-full">
                <div className="bg-blue-600 text-white p-3 rounded-xl rounded-br-none max-w-[75%]">
                  <p className="text-sm sm:text-lg">Tell me about black holes.</p>
                  <p className="text-xs sm:text-base text-blue-200 mt-1 text-right">Vipul, 12:36</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/vipul-avatar.png"
                    alt="User Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
              </div>
              {/* Message 3: Study Buddy */}
              <div className="flex items-start space-x-4 w-full">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/study-buddy-avatar.png"
                    alt="Study Buddy Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
                <div className="bg-gray-100 p-3 rounded-xl rounded-tl-none max-w-[75%]">
                  <p className="text-sm sm:text-lg text-gray-800">
                    Super dense regions where gravity traps everything, even light. Not vacuums!
                  </p>
                  <p className="text-xs sm:text-base text-gray-500 mt-1">Study Buddy, 12:36</p>
                </div>
              </div>
              {/* Message 4: Vipul */}
              <div className="flex items-end justify-end space-x-4 w-full">
                <div className="bg-blue-600 text-white p-3 rounded-xl rounded-br-none max-w-[75%]">
                  <p className="text-sm sm:text-lg">What is the significance of the event horizon?</p>
                  <p className="text-xs sm:text-base text-blue-200 mt-1 text-right">Vipul, 12:37</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/vipul-avatar.png"
                    alt="User Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
              </div>
              {/* Message 5: Study Buddy */}
              <div className="flex items-start space-x-4 w-full">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <img
                    src="/images/study-buddy-avatar.png"
                    alt="Study Buddy Avatar"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                    width={40}
                    height={40}
                  />
                </div>
                <div className="bg-gray-100 p-3 rounded-xl rounded-tl-none max-w-[75%]">
                  <p className="text-sm sm:text-lg text-gray-800">
                    It's the point of no return! Once something crosses it, it can't escape the black hole's gravity.
                  </p>
                  <p className="text-xs sm:text-base text-gray-500 mt-1">Study Buddy, 12:38</p>
                </div>
              </div>
            </div>
            {/* Right side - Content */}
            <div className="space-y-6 relative z-10 order-1 lg:order-2">
              <h3 className="text-3xl sm:text-4xl font-bold text-blue-600">Study Buddy Chat</h3>
              <p
                className="text-gray-600 leading-[150%] text-base sm:text-lg lg:text-[20px]"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                An AI-powered assistant that helps you instantly create engaging teaching materials like mind maps,
                flowcharts, flashcards, and more. Simply enter a topic, and the bot transforms it into visual and
                interactive learning aids.
              </p>
              <button
                onClick={() => scrollToSection("demo-section")}
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 sm:px-8 py-3 rounded-full transition-colors flex items-center space-x-2"
              >
                <span>Try Study Buddy</span>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5"
                >
                  <path
                    d="M12 4L20 12L12 20M4 12H20H4Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* E-Classroom Feature - Content Left, Images Right */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-20">
            <div className="space-y-6 order-2 lg:order-1">
              <h3 className="text-3xl sm:text-4xl font-bold text-blue-600">E-Classroom</h3>
              <p
                className="text-gray-600 leading-[150%] text-base sm:text-lg lg:text-[20px]"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                Host live lectures and group discussions seamlessly—whether you're in the classroom or learning from
                home. Engage students in real-time with interactive sessions and collaborative tools. Get automatically
                generated detailed notes, concise summaries, and key takeaways after each session.
              </p>
              <button
                onClick={() => window.open("https://pathshala.aurait.ai/", "_blank")}
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 sm:px-8 py-3 rounded-full transition-colors"
              >
                Learn More →
              </button>
            </div>
            {/* Right side - Image grid */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6 order-1 lg:order-2">
              {/* Purple book image */}
              <div>
                <img
                  src="/images/purple-book.jpg"
                  alt="Open book with colorful pages"
                  className="w-full h-auto rounded-2xl"
                  width={300}
                  height={200}
                />
              </div>
              {/* Teal e-learning tablet image */}
              <div>
                <img
                  src="/images/elearning-tablet.jpg"
                  alt="E-learning tablet interface"
                  className="w-full h-auto rounded-2xl"
                  width={300}
                  height={200}
                />
              </div>
            </div>
          </div>

          {/* Analytics Dashboard Feature - Image Left, Content Right */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 lg:mb-20">
            <div className="relative order-2 lg:order-1">
              <img
                src="/images/analytics-chart.png"
                alt="Weekly analytics chart"
                className="w-full h-auto rounded-2xl"
                width={600}
                height={400}
              />
            </div>
            <div className="space-y-6 order-1 lg:order-2">
              <h3 className="text-3xl sm:text-4xl font-bold text-blue-600">Analytics Dashboard</h3>
              <p
                className="text-gray-600 leading-[150%] text-base sm:text-lg lg:text-[20px]"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                Monitor every student's progress in real-time with a clear, intuitive dashboard. Track performance
                across assignments, quizzes, and participation—all in one place. Spot trends, identify learning gaps,
                and provide timely support with ease.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials & Proof Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-8 sm:mb-12">Testimonials & Proof</h2>
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 max-w-4xl mx-auto mb-8 sm:mb-12">
            <div className="bg-gray-50 p-6 sm:p-8 rounded-lg shadow-md text-left">
              <p className="text-base sm:text-lg italic text-gray-700 mb-4">
                "Padhai cut my grading workload in half. Now I spend more time teaching and less on paperwork."
              </p>
              <p className="font-semibold text-gray-900">— Priya Sharma, High School English Teacher</p>
            </div>
            <div className="bg-gray-50 p-6 sm:p-8 rounded-lg shadow-md text-left">
              <p className="text-base sm:text-lg italic text-gray-700 mb-4">
                "Our students love Study Buddy. It feels like they have a tutor whenever they need one."
              </p>
              <p className="font-semibold text-gray-900">— Rahul Mehta, Middle School Science Instructor</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo-section" className="py-12 sm:py-20 bg-gray-50 relative">
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center space-y-6 sm:space-y-8 mb-12 sm:mb-20">
            <div className="space-y-4">
              <h3 className="text-3xl sm:text-4xl font-bold">
                <span className="text-blue-600">Experience it now — </span>
                <span className="text-red-500">powerful tools made simple.</span>
              </h3>
              <p className="text-gray-600 text-base sm:text-lg px-4">
                Try out StudyBuddy and Assignment Creator — your AI-powered teaching assistants. Create, plan, and
                manage lessons effortlessly in minutes.
              </p>
            </div>
            {/* Toggle for Study Buddy / Assignment */}
            <div className="flex justify-center gap-4 mb-8">
              <button
                onClick={() => setDemoMode("study-buddy")}
                className={`px-4 sm:px-6 py-2 rounded-full text-base sm:text-lg font-semibold transition-colors ${
                  demoMode === "study-buddy"
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Study Buddy {studyBuddyDemoUsed && <CheckCircle className="inline-block w-4 h-4 ml-1" />}
              </button>
              <button
                onClick={() => setDemoMode("assignment")}
                className={`px-4 sm:px-6 py-2 rounded-full text-base sm:text-lg font-semibold transition-colors ${
                  demoMode === "assignment"
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Assignment {assignmentDemoUsed && <CheckCircle className="inline-block w-4 h-4 ml-1" />}
              </button>
            </div>
            {/* Render the appropriate demo component */}
            <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-2 sm:p-4">
              {showOverallDemoLimit ? (
                <div className="flex flex-col items-center justify-center py-12 text-center h-[400px] sm:h-[600px] border border-gray-200 rounded-lg bg-gray-50">
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Demo Limit Reached!</h4>
                  <p className="text-red-500 font-semibold mb-4">
                    You've used your free demo for both Study Buddy and Assignment.
                  </p>
                  <p className="text-gray-500 text-sm mb-4">Please login/signup to continue using Padhai.</p>
                  <a
                    href="/login"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
                  >
                    Login / Sign Up
                  </a>
                </div>
              ) : demoMode === "study-buddy" ? (
                // NEW: Show demo used message only if it was used at render time
                studyBuddyDemoUsedAtRender ? (
                  <StudyBuddyDemoUsedMessage />
                ) : (
                  <LandingDemoStudyBuddy
                    studyBuddyDemoUsed={studyBuddyDemoUsed}
                    setStudyBuddyDemoUsed={setStudyBuddyDemoUsed}
                  />
                )
              ) : // NEW: Show demo used message only if it was used at render time
              assignmentDemoUsedAtRender ? (
                <AssignmentDemoUsedMessage />
              ) : (
                <LandingDemoAssignment
                  assignmentDemoUsed={assignmentDemoUsed}
                  setAssignmentDemoUsed={setAssignmentDemoUsed}
                />
              )}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-4 mb-8 sm:mb-12">
              <h3 className="text-3xl sm:text-4xl font-bold">
                <span className="text-blue-600">Frequently </span>
                <span className="text-red-500">asked </span>
                <span className="text-blue-600">questions</span>
              </h3>
              <p
                className="text-[#6A6D9E] leading-[150%] text-lg sm:text-[24px] text-center max-w-2xl mx-auto px-4"
                style={{ fontFamily: "Roboto, sans-serif", fontWeight: 400, letterSpacing: "0%" }}
              >
                If you are not sure whether Padhai is suitable for you or not, do not worry. We are here to explain
                everything you might want to know.
              </p>
            </div>
            {/* FAQ Container with blue border */}
            <div className="border-2 border-blue-400 rounded-lg bg-white">
              <div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-1")}
                  >
                    What is Padhai?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-1" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-1" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      Padhai is an online platform that helps teachers make assignments and quizzes in minutes and lets
                      students practice and get feedback right away. It also shows teachers how each student is doing so
                      they can give extra help where it's needed.
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-2")}
                  >
                    What subjects can Padhai help me with?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-2" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-2" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      Right now we cover school-level math, physics, chemistry, biology and English. We'll soon add
                      social studies, computer science and special modules for exams like NEET and JEE.
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-3")}
                  >
                    Is Padhai suitable for all age groups?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-3" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-3" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      Yes. You pick the grade or topic you want, and Padhai will match the question difficulty to each
                      student. It works just as well for younger learners as for older ones.
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-4")}
                  >
                    Can Padhai help me prepare for exams?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-4" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-4" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      Definitely. You can take full mock tests that look like the real thing, drill on topics you find
                      hard and see clear reports on where you need more practice.
                    </div>
                  )}
                </div>
                <div className="border-b border-gray-200 last:border-b-0">
                  <button
                    className="w-full px-4 sm:px-6 py-4 sm:py-6 text-left leading-[150%] text-lg sm:text-[24px] hover:bg-gray-50 flex justify-between items-center transition-colors"
                    style={{ fontFamily: "Roboto, sans-serif", fontWeight: 700, letterSpacing: "0%", color: "#16194F" }}
                    onClick={() => toggleAccordion("item-5")}
                  >
                    How do i get started with Padhai?
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${openAccordion === "item-5" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openAccordion === "item-5" && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 text-gray-600">
                      1. Go to our website and sign up for the free pilot as a student or a teacher. 2. Create your
                      class and pick your subjects and grades. 3. Share the class code or link with your students. 4.
                      Create AI generated Assignments and Quiz 5. Watch their progress online and add more work as you
                      go. If you need any help, our support team and guides are just a click away.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-100 py-8 sm:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Left side - Logo and Social */}
            <div className="space-y-4 sm:space-y-6 col-span-1 sm:col-span-2 lg:col-span-1">
              <div className="logo-name mt-2">
                <a href="/" className="flex items-center gap-2 py-2">
                  <div>
                    <h1 className="flex text-xl sm:text-2xl md:text-3xl font-semibold text-black">
                      <span>पढ़.</span>
                      <span className="gradient-text font-semibold">AI</span>
                    </h1>
                  </div>
                </a>
              </div>
              <div className="flex space-x-4">
                <a
                  href="https://m.facebook.com/profile.php?id=61577298545832&name=xhp_nt__fb__action__open_user"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5 text-gray-500 hover:text-blue-600 cursor-pointer transition-colors" />
                </a>
                <a
                  href="https://www.linkedin.com/company/padhai-by-aura/posts/?feedView=all"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5 text-gray-500 hover:text-blue-600 cursor-pointer transition-colors" />
                </a>
              </div>
              <div className="text-sm text-gray-600">
                <p>Copyright © 2025 Padhai LLC.</p>
                <p>All rights reserved.</p>
              </div>
            </div>
            {/* Navigation Links - Column 1 */}
            <div className="space-y-4">
              <a href="#features-section" className="block text-gray-700 hover:text-blue-600 transition-colors">
                Features
              </a>
              <a href="/bookADemo" className="block text-gray-700 hover:text-blue-600 transition-colors">
                Book a Demo
              </a>
              <button
                onClick={() => setShowPrivacyPolicy(true)}
                className="block text-gray-700 hover:text-blue-600 transition-colors text-left"
              >
                Privacy Policy
              </button>
            </div>
            {/* Navigation Links - Column 2 */}
            <div className="space-y-4">
              <button
                onClick={() => setShowAbout(true)}
                className="block text-gray-700 hover:text-blue-600 transition-colors text-left"
              >
                About
              </button>
              <a href="#" className="block text-gray-700 hover:text-blue-600 transition-colors">
                Blog
              </a>
            </div>
            {/* Contact Information */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2 text-gray-600">
                <Mail className="w-4 h-4" />
                <span className="text-sm sm:text-base">support@aurait.ai</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <Phone className="w-4 h-4" />
                <span className="text-sm sm:text-base">+91 9625697024</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* About Modal */}
      {showAbout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto w-full mx-4">
            <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">About Padhai & Aura</h2>
              <button onClick={() => setShowAbout(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>
            <div className="px-4 sm:px-6 py-6 space-y-6 text-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-blue-600 mb-3">About Padhai</h3>
                <p className="mb-4">
                  Padhai is a revolutionary AI-powered educational platform designed to transform the way teachers
                  create content and students learn. Our mission is to bridge the gap between traditional teaching
                  methods and modern technology, making education more accessible, engaging, and effective.
                </p>
                <p className="mb-4">
                  With Padhai, educators can generate high-quality assignments, quizzes, and educational content in
                  minutes rather than hours. Our AI-powered Study Buddy provides personalized learning assistance, while
                  our comprehensive analytics dashboard helps track student progress in real-time.
                </p>
                <p>
                  We currently support core subjects including Mathematics, Physics, Chemistry, Biology, and English for
                  school-level education, with plans to expand to social studies, computer science, and specialized exam
                  preparation modules for NEET and JEE.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-blue-600 mb-3">About Aura AI Technologies</h3>
                <p className="mb-4">
                  Aura AI Technologies, Inc. is the innovative company behind Padhai. Founded with the vision of
                  democratizing quality education through artificial intelligence, we are committed to creating tools
                  that empower educators and enhance learning experiences.
                </p>
                <p className="mb-4">
                  Our team consists of experienced educators, AI researchers, and technology experts who understand the
                  challenges faced by both teachers and students in today's educational landscape. We combine
                  cutting-edge AI technology with deep pedagogical insights to create solutions that truly make a
                  difference.
                </p>
                <p>
                  At Aura, we believe that technology should enhance human potential, not replace it. Our AI tools are
                  designed to free up educators' time from repetitive tasks, allowing them to focus on what they do best
                  – inspiring and guiding students.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-blue-600 mb-3">Our Vision</h3>
                <p className="mb-4">
                  To create a world where quality education is accessible to everyone, regardless of geographical
                  location or economic background. We envision classrooms where AI assists teachers in providing
                  personalized learning experiences for every student.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-blue-600 mb-3">Our Mission</h3>
                <p className="mb-4">
                  To empower educators with AI-driven tools that simplify content creation, enhance student engagement,
                  and provide actionable insights into learning progress. We strive to make teaching more efficient and
                  learning more effective through innovative technology solutions.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-blue-600 mb-3">Key Features</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>AI-Powered Question Generation:</strong> Create assignments, quizzes, and essays instantly
                    from any topic or material
                  </li>
                  <li>
                    <strong>Study Buddy Chat:</strong> 24/7 AI assistant for personalized learning support
                  </li>
                  <li>
                    <strong>E-Classroom:</strong> Host live lectures and interactive sessions with automatic note
                    generation
                  </li>
                  <li>
                    <strong>Analytics Dashboard:</strong> Real-time tracking of student progress and performance
                    insights
                  </li>
                  <li>
                    <strong>Multi-Subject Support:</strong> Comprehensive coverage of core academic subjects
                  </li>
                  <li>
                    <strong>Adaptive Learning:</strong> Personalized difficulty levels based on individual student needs
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-blue-600 mb-3">Contact Information</h3>
                <div className="space-y-2">
                  <p>
                    <strong>Company:</strong> Aura AI Technologies, Inc.
                  </p>
                  <p>
                    <strong>Email:</strong> support@aurait.ai
                  </p>
                  <p>
                    <strong>Phone:</strong> +91 9625697024
                  </p>
                  <p>
                    <strong>Website:</strong> padhai.ai
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Ready to transform your teaching experience?</strong> Join thousands of educators who are
                  already using Padhai to create engaging content and track student progress effortlessly.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto w-full mx-4">
            <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Privacy Policy</h2>
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="px-4 sm:px-6 py-6 space-y-6 text-gray-700">
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  <strong>Last Updated:</strong> 21 May 2025
                  <br />
                  <strong>Effective Date:</strong> 21 May 2025
                </p>

                <p>
                  Hey there! Welcome to Aura AI Technologies, Inc. ("AuraAI," "we," "us," or "our"). We care about your
                  privacy and want to keep things simple, clear, and human-friendly. This Privacy Policy explains how we
                  handle your info when you use <strong>PadhAI</strong> – our AI-powered learning buddy that helps with
                  quizzes, auto-grading, live classes, and personalized study recommendations.
                </p>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">What This Means For You</h3>
                  <p>We want you to know exactly what we collect, why we collect it, and how you stay in control.</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">1. What We Collect (and Why)</h3>

                  <h4 className="font-semibold mb-2">Info You Give Us</h4>
                  <ul className="list-disc pl-6 space-y-1 mb-4">
                    <li>
                      <strong>Account Basics:</strong> Name, email, phone, education level, preferences.
                    </li>
                    <li>
                      <strong>Payments:</strong> To process subscriptions, courses, or in-app purchases.
                    </li>
                    <li>
                      <strong>Learning Data:</strong> Notes, assignments, questions, or content you create in the app.
                    </li>
                    <li>
                      <strong>Support Requests:</strong> Info you share when you contact us for help.
                    </li>
                  </ul>

                  <h4 className="font-semibold mb-2">Info We Collect Automatically</h4>
                  <ul className="list-disc pl-6 space-y-1 mb-4">
                    <li>
                      <strong>App Usage:</strong> Features you use, your learning progress, quiz results, and session
                      times.
                    </li>
                    <li>
                      <strong>Device Details:</strong> Device type, operating system, unique IDs, IP address (for
                      security), and crash reports.
                    </li>
                    <li>
                      <strong>Cookies & Analytics:</strong> To keep you logged in, remember your preferences, and
                      understand how PadhAI is performing.
                    </li>
                  </ul>

                  <h4 className="font-semibold mb-2">Info From Other Places</h4>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>
                      <strong>Payment Processors:</strong> To confirm transactions.
                    </li>
                    <li>
                      <strong>Login Providers:</strong> Like Google or Apple, if you choose them for signing in.
                    </li>
                    <li>
                      <strong>Analytics Tools:</strong> To improve how fast and helpful PadhAI is.
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">2. How We Use Your Info</h3>
                  <p>We use your info to:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Run and personalize PadhAI for you.</li>
                    <li>Process payments and manage subscriptions.</li>
                    <li>Give AI-powered insights and learning recommendations.</li>
                    <li>Send updates and helpful tips.</li>
                    <li>Keep things safe and prevent fraud.</li>
                    <li>Follow laws and regulations.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">3. Cookies (No, Not the Edible Kind)</h3>
                  <p className="mb-2">We use cookies and similar tech to:</p>
                  <ul className="list-disc pl-6 space-y-1 mb-2">
                    <li>Keep you logged in securely.</li>
                    <li>Remember your learning settings.</li>
                    <li>Make PadhAI smarter by seeing how features are used.</li>
                  </ul>
                  <p>
                    You can turn off cookies in your device settings, but some features might stop working as expected.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Who We Share Your Info With</h3>
                  <p className="mb-2">We don't sell your data. Period. We only share it with:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>
                      <strong>Trusted Partners:</strong> For hosting, payments, analytics, and support.
                    </li>
                    <li>
                      <strong>Legal Requirements:</strong> If required by law.
                    </li>
                    <li>
                      <strong>Business Changes:</strong> Like mergers or acquisitions.
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">5. How Long We Keep It</h3>
                  <p>We keep your info:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>While you have an account.</li>
                    <li>As long as we need it for business or legal reasons.</li>
                  </ul>
                  <p>Then we delete or anonymize it safely.</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Keeping Your Info Safe</h3>
                  <p>
                    We use industry-standard security (encryption, access controls) to protect your data. No system is
                    100% secure, but we do our best. If you think something's wrong, email us at{" "}
                    <strong>support@aurait.ai</strong>.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Your Rights (You're in Control)</h3>
                  <p className="mb-2">You can:</p>
                  <ul className="list-disc pl-6 space-y-1 mb-2">
                    <li>
                      <strong>Access:</strong> Get a copy of your data.
                    </li>
                    <li>
                      <strong>Correct:</strong> Fix mistakes in your info.
                    </li>
                    <li>
                      <strong>Delete:</strong> Ask us to delete your account and data.
                    </li>
                    <li>
                      <strong>Opt-Out:</strong> Say no to marketing emails.
                    </li>
                  </ul>
                  <p>
                    Just email <strong>support@aurait.ai</strong> to do any of this.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">8. India-Specific Stuff</h3>
                  <p className="mb-2">
                    We follow the <strong>Digital Personal Data Protection Act, 2023 (DPDP)</strong> and relevant IT
                    laws. Indian users can:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 mb-2">
                    <li>Request data access or corrections.</li>
                    <li>Ask for deletion of their data.</li>
                    <li>Opt-out of non-essential marketing.</li>
                  </ul>
                  <p>We respond to these requests in line with Indian law.</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Questions?</h3>
                  <p className="mb-2">Contact us at:</p>
                  <p>
                    <strong>Aura AI Technologies, Inc.</strong>
                    <br />
                    <strong>Email:</strong> support@aurait.ai
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">10. The Legal Bit</h3>
                  <p>This policy is governed by Indian law. Any disputes go to courts in India.</p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>In short:</strong> We collect only what we need to make PadhAI awesome for you, keep it
                    safe, and give you full control. No shady stuff. Ever.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
